import React, { memo, useEffect, useState } from "react";
import { FormikProps, Form, useFormikContext } from "formik";
import { Grid, RadioGroup, FormControl, FormControlLabel, Box, Button } from "@mui/material";
import pageStyles from "../../pageStyles";
import { Data } from "../../../store/pos/types";
import { PosAuthentication, PosInterfaces } from "./PosConstants";
import FormSubmitButtons from "../../../common/buttons/FormSubmitButtons";
import NexoFields from "./NexoFields";
import OpiFields from "./OpiFields";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import CustomizedRadio from "./../../../common/formElements/RadioButton";
import CustomizedCheckbox from "./../../../common/formElements/Checkbox";
import TextInput from "../../../common/formElements/TextInput";
import { useToastMessage } from "../../../effects/useToastMessage";
import KEKFields from "./KEKFields";
import { Add } from "@mui/icons-material";
import KEKFieldsAutofill from "./KEKFieldsAutofill";

const PosEditForm = () => {
  const formikProps = useFormikContext();
  const {
    values,
    setFieldValue,
    errors,
    isSubmitting,
  } = formikProps as FormikProps<Data>;
  const classes = pageStyles();

  // Show toast errors
  useToastMessage(isSubmitting, errors);

  const printOnPOSDisabled = values.attributes.Interface === PosInterfaces.OPI;

  const handleInterfaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("attributes.Interface", e.target.value);
  };

  const handleAuthenticationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("attributes.AuthenticationType", e.target.value);
  };

  //save current KEK state
  const [kek, setKek] = useState(values.attributes.KEK);

  //update KEK state
  useEffect(() => {
    setFieldValue("attributes.KEK", kek);
  }, [kek, setFieldValue]);

  console.log(values.attributes.KEK);

  //generate random KEK
  function generateKek(length: number) {
    const characters = "0123456789ABCDEF";
    let result = "";
  
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters.charAt(randomIndex);
    }

    return result;
  }

  return (
    <Form style={{ width: "100%" }}>
      <SectionWrapper title="Interface" isChild>
        <Grid
          container
          alignItems="flex-start"
          spacing={3}
          className={classes.formWrapper as string}
        >
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup
                name="attributes.Interface"
                value={values.attributes.Interface}
                onChange={handleInterfaceChange}
                row
              >
                {Object.keys(PosInterfaces).map((posInterface) => (
                  <CustomizedRadio
                    key={posInterface}
                    value={posInterface}
                    label={PosInterfaces[posInterface]}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={5}>
            <TextInput
              fieldname="attributes.POSDesc"
              label="POS Description*"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <CustomizedCheckbox
                  checked={printOnPOSDisabled || values.attributes.PrintOnPOS}
                  onChange={(
                    _event: React.FormEvent<HTMLInputElement>,
                    data: boolean
                  ) => {
                    setFieldValue("attributes.PrintOnPOS", data);
                  }}
                  name="attributes.PrintOnPOS"
                  disabled={printOnPOSDisabled}
                />
              }
              label="Print on POS"
            />
            <FormControlLabel
              control={
                <CustomizedCheckbox
                  checked={values.attributes.PrintOnTerminal}
                  onChange={(_event, data: boolean) => {
                    setFieldValue("attributes.PrintOnTerminal", data);
                  }}
                  name="attributes.PrintOnTerminal"
                />
              }
              label="Print on Terminal"
            />
          </Grid>
          </Grid>
      </SectionWrapper>
      <SectionWrapper title="Authentication" isChild>
        <Grid
          container
          alignItems="flex-start"
          spacing={3}
          className={classes.formWrapper}
        >
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <Box pb={2}>
                <RadioGroup
                  name="attributes.AuthenticationType"
                  value={values.attributes.AuthenticationType}
                  onChange={handleAuthenticationChange}
                  row
                >
                  {Object.keys(PosAuthentication).map((posAuthentication) => (
                    <CustomizedRadio
                      key={posAuthentication}
                      value={posAuthentication}
                      label={PosAuthentication[posAuthentication]}
                    />
                  ))}
                </RadioGroup>
              </Box>
            </FormControl>
            
            <Grid item xs={12} md={12}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                <TextInput
                    fieldname="attributes.KEK"
                    label="KEK"
                    //disabled
                />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <Button
                      color="primary"
                      variant="contained"
                      size="medium"
                      endIcon={<Add />}
                      onClick={() => setKek(generateKek(48))}
                      style={{ marginTop: "15px" }}
                      >
                      Create KEK
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Grid item xs={12} md={12}>
                    <Grid container spacing={2}>
                    {values.attributes.AuthenticationType === PosAuthentication.LOCAL ? (
                        <KEKFieldsAutofill key="kekfields-autofill" />
                      ) : (
                      <KEKFields key="kekfields" />
                    )}

                    </Grid>
                  </Grid>
                </Grid>
            </Grid>
          </Grid>
          </Grid>
          <FormSubmitButtons />
        </Grid>
      </SectionWrapper>

      <SectionWrapper title="POS Info" isChild>
        <>
          {values.attributes.Interface === PosInterfaces.NEXO && <NexoFields />}
          {values.attributes.Interface === PosInterfaces.OPI && <OpiFields />}
        </>
      </SectionWrapper>
    </Form>
  );
};

export default memo(PosEditForm);
